import {
  isManagedBlobUrl,
  registerBlobUrl,
  revokeCurrentBlobUrl,
} from "@/shared/lib/document-view";
import {
  isInIframe,
  isUnifiedRoute,
  requestUnifiedDownload,
  requestUnifiedOpen,
} from "@/shared/utils";
import { buildFileStoreUrl } from "./build-file-store-url";
import type { DocumentSource } from "./document-source";
import { fetchFilestoreFileBlob } from "./fetch-filestore-file-blob";
import { fetchS3FileBlob } from "./fetch-s3-file-blob";
import { fetchS3FileUrl } from "./fetch-s3-file-url";
import {
  isFilestoreDocumentPath,
  normalizeFilestorePath,
} from "./filestore-path";

export type SetPdfFn = (file: {
  url: string;
  fileName: string;
  fileId?: string;
}) => void;

function isUnifiedMode(): boolean {
  return typeof window !== "undefined" && isUnifiedRoute(window.location.pathname);
}

function postReadFileToParent(fileUrl: string, fileName: string) {
  window.parent.postMessage(
    {
      type: "read-file",
      fileUrl,
      fileName: fileName || "document.pdf",
    },
    "*",
  );
}

function downloadByUrl(fileUrl: string, fileName: string) {
  requestUnifiedDownload({ fileUrl, fileName });
}

async function toPreviewBlobUrl(fileUrl: string): Promise<string> {
  if (fileUrl.startsWith("blob:")) {
    return registerBlobUrl(fileUrl);
  }

  if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
    const response = await fetch(fileUrl);

    if (!response.ok) {
      throw new Error("Не удалось загрузить документ");
    }

    const blob = await response.blob();

    if (blob.size === 0) {
      throw new Error("Не удалось загрузить документ");
    }

    return registerBlobUrl(URL.createObjectURL(blob));
  }

  throw new Error("Не удалось загрузить документ");
}

async function loadS3FileBlob(fileId: string): Promise<Blob> {
  try {
    return await fetchS3FileBlob(fileId);
  } catch {
    const fileUrl = await fetchS3FileUrl(fileId);
    const response = await fetch(fileUrl);

    if (!response.ok) {
      throw new Error("Не удалось загрузить документ");
    }

    const blob = await response.blob();

    if (blob.size === 0) {
      throw new Error("Не удалось загрузить документ");
    }

    return blob;
  }
}

async function openS3InLegacyViewer(
  fileId: string,
  fileName: string,
  setPDF: SetPdfFn,
) {
  revokeCurrentBlobUrl();
  const blob = await loadS3FileBlob(fileId);

  setPDF({
    url: registerBlobUrl(URL.createObjectURL(blob)),
    fileName,
    fileId,
  });
}

async function openInLocalViewer(
  fileUrl: string,
  fileName: string,
  setPDF: SetPdfFn,
  fileId?: string,
) {
  revokeCurrentBlobUrl();
  const previewUrl = await toPreviewBlobUrl(fileUrl);

  setPDF({
    url: previewUrl,
    fileName,
    fileId,
  });
}

/** filestore / presigned URL */
export async function openDocumentByUrl(
  fileUrl: string,
  fileName: string,
  setPDF?: SetPdfFn,
) {
  if (!fileUrl) {
    return;
  }

  const resolvedFileName = fileName || "document.pdf";

  if (isFilestoreDocumentPath(fileUrl)) {
    const fullUrl = buildFileStoreUrl(normalizeFilestorePath(fileUrl));

    if (isUnifiedMode()) {
      if (isInIframe()) {
        postReadFileToParent(fullUrl, resolvedFileName);
      }
      return;
    }

    if (!setPDF) {
      return;
    }

    const blob = await fetchFilestoreFileBlob(fileUrl);
    setPDF({
      url: registerBlobUrl(URL.createObjectURL(blob)),
      fileName: resolvedFileName,
    });
    return;
  }

  if (isUnifiedMode()) {
    downloadByUrl(fileUrl, resolvedFileName);
    return;
  }

  if (!setPDF) {
    return;
  }

  await openInLocalViewer(fileUrl, resolvedFileName, setPDF);
}

/** S3 fileId */
export async function openDocumentByFileId(
  fileId: string,
  fileName: string,
  setPDF?: SetPdfFn,
) {
  if (!fileId) {
    throw new Error("fileId is required");
  }

  const resolvedFileName = fileName || "document.pdf";

  if (isUnifiedMode()) {
    const fileUrl = await fetchS3FileUrl(fileId);

    if (isInIframe()) {
      requestUnifiedOpen({ fileUrl, fileName: resolvedFileName });
      return;
    }

    downloadByUrl(fileUrl, resolvedFileName);
    return;
  }

  if (!setPDF) {
    return;
  }

  await openS3InLegacyViewer(fileId, resolvedFileName, setPDF);
}

/** Локальный blob */
export function openLocalBlobUrl(
  blobUrl: string,
  fileName: string,
  setPDF?: SetPdfFn,
) {
  if (!blobUrl || (isUnifiedMode() && isInIframe())) {
    return;
  }

  if (!setPDF) {
    return;
  }

  revokeCurrentBlobUrl();
  setPDF({
    url:
      blobUrl.startsWith("blob:") && !isManagedBlobUrl(blobUrl)
        ? registerBlobUrl(blobUrl)
        : blobUrl,
    fileName: fileName || "document.pdf",
  });
}

export async function openDocument(
  source: DocumentSource,
  fileName: string,
  setPDF?: SetPdfFn,
) {
  switch (source.kind) {
    case "filestore":
      await openDocumentByUrl(source.path, fileName, setPDF);
      break;
    case "s3":
      await openDocumentByFileId(source.fileId, fileName, setPDF);
      break;
    case "presigned-url":
      await openDocumentByUrl(source.url, fileName, setPDF);
      break;
    case "local":
      openLocalBlobUrl(source.blobUrl, fileName, setPDF);
      break;
    default:
      break;
  }
}
